import { useRef, useState } from 'react';
import StatusBreakdownTable from './StatusBreakdownTable';
import CollapsibleSection from './CollapsibleSection';

// Shared by MyHomePM.js and SupplierPortal.js's "Assigned Programs & Lead
// Status" section (and ProgramPickerButton-style pickers) — the two roles'
// program-status pills used to be two separately hand-copied maps
// (STATUS_COLORS / PROGRAM_STATUS_COLORS) with identical values. One source
// now; import this instead of redefining it locally.
export const PROGRAM_STATUS_COLORS = {
  'Pending Kickoff': '#e67e22',
  'Spinup':          '#3498db',
  'WIP':             '#9b59b6',
  'Completed':       '#27ae60',
};

function fmtDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Goal/Target/Total are always shown for the unified per-goal breakdown —
// both roles get the same detail (see statusBreakdown.js's
// goalsToPerGoalRows/supplierGoalsToRows).
const EXTRA_COLUMNS = [
  { key: 'goal', header: 'Goal', insertAfter: 'label', render: r => r.goalName },
  { key: 'target', header: 'Target', insertAfter: 'label', render: r => (r.qty != null ? r.qty : '—') },
  {
    key: 'total', header: 'Total',
    render: r => <span style={{ fontWeight: '700', color: r.total > 0 ? '#2c3e50' : '#aaa' }}>{r.total ?? 0}</span>,
    renderTotal: rows => rows.reduce((s, r) => s + (r.total || 0), 0),
  },
];

function ProgramTile({ item, selected, onClick }) {
  const p = item.program;
  const pct = p.goal_count ? Math.round((p.accepted_count / p.goal_count) * 100) : null;
  return (
    <div
      onClick={onClick}
      style={{
        flex: '0 0 230px', background: 'white', borderRadius: '8px', cursor: 'pointer',
        border: selected ? '2px solid #3498db' : '1px solid #e8ecef',
        padding: selected ? '12px 14px' : '13px 15px',
      }}
    >
      <div style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>{p.program_name}</div>
      {p.client_name && <div style={{ fontSize: '10.5px', color: '#aaa', marginBottom: '2px' }}>{p.client_name}</div>}
      {p.flight_date_start && (
        <div style={{ fontSize: '10px', color: '#7f8c8d', marginBottom: '7px' }}>
          {fmtDate(p.flight_date_start)} → {fmtDate(p.flight_date_end) || '…'}
        </div>
      )}
      {p.status && (
        <span style={{ display: 'inline-block', background: PROGRAM_STATUS_COLORS[p.status] || '#95a5a6', color: 'white', borderRadius: '4px', padding: '2px 7px', fontSize: '10.5px', marginBottom: '8px' }}>
          {p.status}
        </span>
      )}
      {pct != null && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', color: '#7f8c8d', marginBottom: '3px' }}>
            <span>Client accepted</span>
            <span style={{ fontWeight: '700', color: '#2c3e50' }}>{p.accepted_count} / {p.goal_count}</span>
          </div>
          <div style={{ height: '4px', borderRadius: '3px', background: '#eee', overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${Math.min(pct, 100)}%`, background: '#27ae60' }} />
          </div>
        </div>
      )}
    </div>
  );
}

function ProgramDetail({ item }) {
  const { program: p, rows, includesCallReview, renderStatusHeader, headerActions, clientLists } = item;
  return (
    <div style={{ background: 'white', border: '1px solid #e8ecef', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: '#f8f9fa', borderBottom: '1px solid #e8ecef', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>{p.program_name}</span>
        {headerActions && <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{headerActions}</div>}
      </div>
      {clientLists && clientLists.length > 0 && (
        <div style={{ padding: '0 14px' }}>
          <CollapsibleSection label="Client Lists" count={clientLists.length} bodyStyle={{ padding: '10px 0 12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{clientLists}</div>
          </CollapsibleSection>
        </div>
      )}
      {rows === undefined ? (
        <div style={{ padding: '16px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>Loading breakdown…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '16px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>No goals for this program.</div>
      ) : (
        <StatusBreakdownTable
          rows={rows}
          includesCallReview={includesCallReview}
          extraColumns={EXTRA_COLUMNS}
          renderStatusHeader={renderStatusHeader}
          compact
        />
      )}
    </div>
  );
}

// A tile-shaped toggle for revealing closed programs inline at the end of
// the same scrollable row (2026-09-01 — was its own separate carousel strip
// below, with its own scroll buttons and a blank vertical gap above it;
// merged into the one row so "closed" is more tiles in the same carousel,
// not a second component to learn).
function ClosedToggleTile({ open, count, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        flex: '0 0 84px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '3px', background: '#f8f9fa', border: '1px dashed #ccd3d8', borderRadius: '8px', cursor: 'pointer',
        color: '#8a93a1', fontSize: '11px', textAlign: 'center', padding: '13px 8px',
      }}
    >
      <span style={{ fontSize: '13px' }}>{open ? '▼' : '▶'}</span>
      <span>Closed ({count})</span>
    </div>
  );
}

const navBtnStyle = { border: '1px solid #ccd3d8', background: 'white', color: '#2c3e50', width: '26px', height: '26px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };

/**
 * "Assigned Programs & Lead Status" as a horizontally-scrolling row of
 * condensed program tiles, several visible at once, instead of a vertical
 * stack of full status cards (2026-08-31, real ask, wireframed first —
 * "the condensed program & lead status carousel" — see the artifact this
 * shipped from). Clicking a tile doesn't expand the tile itself; it adds
 * (or removes) that program's full per-goal StatusBreakdownTable to a
 * stacked area below the whole carousel — multiple programs can be
 * expanded at once (confirmed via the wireframe: "several at once" /
 * "multi-select is fine"), an "Expand all"/"Collapse all" toggle selects
 * every program in one click, and small ‹/› buttons scroll the strip since
 * native horizontal scroll alone is easy to miss on a row that's often
 * wider than the viewport.
 *
 * Replaces the old ProgramStatusCard (one card per program, each with its
 * own click-to-expand-inline breakdown) — that component is gone; both of
 * its only two callers (this file's two consumers) now build an `items`
 * array instead of a list of <ProgramStatusCard> elements, keeping every
 * per-program slot prop (rows/onExpand/includesCallReview/
 * renderStatusHeader/headerActions) exactly as flexible as before.
 *
 * Props:
 *   items: [{ id, program: {id, program_name, client_name?, status,
 *     flight_date_start?, flight_date_end?, accepted_count?, goal_count?},
 *     rows?, onExpand?(id), includesCallReview, renderStatusHeader?,
 *     headerActions?, clientLists? }] — active programs, always shown in the strip.
 *     clientLists (2026-09-02) is an array of rendered nodes (e.g. download
 *     buttons) shown in a "Client Lists" sub-section, collapsed by default,
 *     directly under the title row — kept separate from headerActions
 *     (always visible) since a program with several assigned lists made
 *     that row too busy. Omitted/empty renders nothing.
 *   closedItems?: same shape — collapsed behind an inline "▶ Closed (N)"
 *     tile at the end of the SAME strip (2026-09-01 — was a second,
 *     separate carousel with its own scroll buttons and a gap above it;
 *     merged into one row so revealing closed programs doesn't mean
 *     learning a second carousel). Selecting one still expands into the
 *     same shared detail area below. Excluded from "Expand all" — that
 *     control only ever selects/deselects the active `items` (closed
 *     programs are opt-in one at a time, never swept in as a side effect
 *     of expanding everything active).
 *   emptyText?: shown instead of the carousel when `items` is empty.
 */
export default function ProgramCarousel({ items, closedItems = [], emptyText = 'No programs assigned.' }) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [closedOpen, setClosedOpen] = useState(false);
  const scrollRef = useRef(null);

  const allItems = items.concat(closedItems);

  const maybeExpand = (item) => {
    if (item.rows === undefined && item.onExpand) item.onExpand(item.id);
  };

  const toggle = (item) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
        maybeExpand(item);
      }
      return next;
    });
  };

  // Scoped to active `items` only — a closed program a PM/supplier opened
  // by hand stays open/closed independently of this control.
  const allSelected = items.length > 0 && items.every(it => selectedIds.has(it.id));
  const toggleAll = () => {
    if (!allSelected) items.forEach(maybeExpand);
    setSelectedIds(prev => {
      const next = new Set(prev);
      items.forEach(it => (allSelected ? next.delete(it.id) : next.add(it.id)));
      return next;
    });
  };

  const scrollBy = (dx) => scrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' });

  if (items.length === 0 && closedItems.length === 0) {
    return (
      <div style={{ padding: '20px', background: 'white', border: '1px solid #eee', borderRadius: '8px', color: '#aaa', textAlign: 'center', fontSize: '13px' }}>
        {emptyText}
      </div>
    );
  }

  const selectedItems = allItems.filter(it => selectedIds.has(it.id));
  const rowHasContent = items.length > 0 || (closedOpen && closedItems.length > 0);

  return (
    <div>
      {items.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
          <button
            onClick={toggleAll}
            style={{
              border: '1px solid #95a5a6', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer',
              background: allSelected ? '#2c3e50' : 'white', color: allSelected ? 'white' : '#555',
            }}
          >
            {allSelected ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <div ref={scrollRef} style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '4px', scrollbarWidth: 'none' }}>
          {items.map(item => (
            <ProgramTile key={item.id} item={item} selected={selectedIds.has(item.id)} onClick={() => toggle(item)} />
          ))}
          {closedItems.length > 0 && (
            <ClosedToggleTile open={closedOpen} count={closedItems.length} onClick={() => setClosedOpen(o => !o)} />
          )}
          {closedOpen && closedItems.map(item => (
            <ProgramTile key={item.id} item={item} selected={selectedIds.has(item.id)} onClick={() => toggle(item)} />
          ))}
        </div>
        {rowHasContent && (
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '6px' }}>
            <button onClick={() => scrollBy(-240)} aria-label="Scroll left" style={navBtnStyle}>‹</button>
            <button onClick={() => scrollBy(240)} aria-label="Scroll right" style={navBtnStyle}>›</button>
          </div>
        )}
      </div>

      {selectedItems.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
          {selectedItems.map(item => <ProgramDetail key={item.id} item={item} />)}
        </div>
      )}
    </div>
  );
}
